/**
 * Command Palette (Cmd+K / Ctrl+K) — universal overlay for navigation,
 * mode switching, recent items, and quick actions across LexIDE.
 *
 * v1 scope (Phase 6.3):
 * - Mode switching (Home, Workspace, Research, Activity, Organization, Settings)
 * - Recent items from useNavigationStore
 * - Recent cases from useCases (TanStack Query)
 * - Quick actions (Lista de casos, Nova conversa)
 *
 * Deferred:
 * - Global search across documents (search.global RPC)
 * - In-case search (Cmd+Shift+F)
 * - Semantic search via CaseIndex embeddings
 * - Document search (Cmd+F within a document)
 */

import { caseStatusLabel } from "@/lib/case-status";
import { useChatUnreadStore } from "@/stores/useChatUnreadStore";
import { useCallback, useEffect } from "react";
import { Command } from "cmdk";
import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Bell,
  Building2,
  Clock,
  FileText,
  FolderOpen,
  Home,
  MessageSquarePlus,
  Plus,
  Settings,
} from "lucide-react";
import { useCommandPaletteStore } from "@/stores/useCommandPaletteStore";
import { useUIStore, type AppMode } from "@/stores/useUIStore";
import { useCases } from "@/hooks/useCases";
import { useNavigationStore } from "@/hooks/useNavigation";
import { useActiveContextStore } from "@/stores/useActiveContextStore";
import { homeTarget } from "@/lib/landing-decision";
import { cn } from "@/lib/utils";

interface ModeAction {
  id: string;
  /** i18n key under `nav.*` — shared with the ActivityBar rail labels. */
  labelKey: string;
  /** i18n key under `commandPalette.mode*Hint` — secondary descriptive text. */
  hintKey: string;
  icon: typeof Home;
  mode: AppMode;
  path: string;
}

const MODE_ACTIONS: ModeAction[] = [
  { id: "mode-home", labelKey: "nav.home", hintKey: "commandPalette.modeHomeHint", icon: Home, mode: "home", path: "/home" },
  // D6 broadening: workspace tree replaces the legacy /cases flat list
  // (App.tsx:101 marks /cases as "preserved as a fallback; remove after D7").
  { id: "mode-workspace", labelKey: "nav.workspace", hintKey: "commandPalette.modeWorkspaceHint", icon: FolderOpen, mode: "cases", path: "/workspace" },
  { id: "mode-activity", labelKey: "nav.activity", hintKey: "commandPalette.modeActivityHint", icon: Bell, mode: "activity", path: "/activity" },
  { id: "mode-org", labelKey: "nav.organization", hintKey: "commandPalette.modeOrgHint", icon: Building2, mode: "org", path: "/org" },
  { id: "mode-settings", labelKey: "nav.settings", hintKey: "commandPalette.modeSettingsHint", icon: Settings, mode: "settings", path: "/settings" },
];

const groupHeadingClasses =
  "px-1 py-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-caption [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-cream-500";

export function CommandPalette() {
  const { t } = useTranslation();
  const open = useCommandPaletteStore((s) => s.open);
  const setOpen = useCommandPaletteStore((s) => s.setOpen);
  const setActiveMode = useUIStore((s) => s.setActiveMode);
  const navigate = useNavigate();

  // P6 item 3 — unread dots on the case rows (same store OverviewNav reads).
  const unreadByCase = useChatUnreadStore((s) => s.unreadByCase);
  // Only fetch the case list when the palette is open. Cases share the
  // ["cases"] query key so any data already cached by a sibling consumer
  // (Home dashboard widgets, CasesListLayout) is reused on first open.
  const { data: cases, isLoading: casesLoading } = useCases({ enabled: open });

  // Recent navigation history (last 20 visited items) for the "Recentes" group.
  const recentItems = useNavigationStore((s) => s.recentItems);

  const handleOpenChange = useCallback((next: boolean) => setOpen(next), [setOpen]);

  // Shared navigation helper — closes the palette and routes.
  const navigateTo = useCallback(
    (path: string, mode: AppMode) => {
      setActiveMode(mode);
      navigate(path);
      setOpen(false);
    },
    [setActiveMode, navigate, setOpen],
  );

  const goToMode = useCallback(
    (action: ModeAction) =>
      navigateTo(
        // D-3 (miniplan H2) — Home resolves to the lawyer's desk, exactly as the
        // rail's Home entry does. Kept in step deliberately: two "Home"
        // affordances that disagree about where Home is would be worse than
        // either behaviour on its own.
        action.mode === "home"
          ? homeTarget({
              selection: useActiveContextStore.getState().selection,
              landing: useUIStore.getState().landing,
            })
          : action.path,
        action.mode,
      ),
    [navigateTo],
  );

  const openCase = useCallback(
    (caseId: string) => navigateTo(`/cases/${caseId}`, "cases"),
    [navigateTo],
  );

  const openRecent = useCallback(
    (path: string) => {
      // Best-effort: derive a sensible AppMode from the path; default to "home"
      // when the path doesn't map to a registered mode (e.g. probe routes).
      const mode: AppMode = path.startsWith("/cases") || path.startsWith("/clients") || path.startsWith("/workspace")
        ? "cases"
        : path.startsWith("/activity")
          ? "activity"
          : path.startsWith("/org")
            ? "org"
            : path.startsWith("/settings")
              ? "settings"
                : "home";
      navigateTo(path, mode);
    },
    [navigateTo],
  );

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  if (!open) return null;

  // Top 5 recents that are not the current path
  const topRecents = recentItems.slice(0, 5);

  return (
    <Command.Dialog
      open={open}
      onOpenChange={handleOpenChange}
      label={t("commandPalette.dialogTitle")}
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label={t("commandPalette.closeAriaLabel")}
        onClick={() => setOpen(false)}
        className="absolute inset-0 bg-coffee-900/40 backdrop-blur-sm dark:bg-coffee-950/60"
      />

      {/* Dialog — elevated overlay, so it carries the §4 soft warm shadow
          (the one place flat-surface shadows are allowed). */}
      <div className="relative z-10 w-full max-w-[640px] overflow-hidden rounded-2xl border border-cream-200/60 bg-white shadow-[0_1px_2px_rgba(28,24,20,0.04),0_8px_24px_rgba(28,24,20,0.06)] dark:border-coffee-700/60 dark:bg-coffee-900">
        <VisuallyHidden>
          <Dialog.Title>{t("commandPalette.dialogTitle")}</Dialog.Title>
          <Dialog.Description>{t("commandPalette.dialogDescription")}</Dialog.Description>
        </VisuallyHidden>
        <Command label={t("commandPalette.dialogTitle")} loop className="flex flex-col">
          {/* Small serif title (plan §6.5) + the borderless search input.
              The input row carries a hairline bottom border that goes
              gold on focus — the §6.5 "gold focus underline". */}
          <div className="px-4 pb-2 pt-4">
            <h2 className="font-serif-display text-display-sm text-coffee-900 dark:text-cream-100">
              {t("commandPalette.dialogTitle")}
            </h2>
          </div>
          <div className="mx-4 flex items-center gap-2 border-b border-cream-200/60 pb-2.5 transition-colors focus-within:border-gold-500/40 dark:border-coffee-700/60">
            <Command.Input
              autoFocus
              placeholder={t("commandPalette.inputPlaceholder")}
              className="flex-1 bg-transparent text-body-lg text-coffee-900 placeholder:text-cream-500 focus:outline-none dark:text-cream-100 dark:placeholder:text-coffee-500"
            />
            <KbdHint>Esc</KbdHint>
          </div>

          <Command.List className="max-h-[60vh] overflow-y-auto p-1">
            <Command.Empty className="px-4 py-6 text-center text-body-sm text-cream-500">
              {t("commandPalette.emptyState")}
            </Command.Empty>

            {/* Quick actions */}
            <Command.Group heading={t("commandPalette.groupActions")} className={groupHeadingClasses}>
              {/*
                NOTE: "Lista de casos" navigates to the cases list. A real
                "create case" dialog launch is deferred — once CasesListLayout
                exposes a programmatic create entry point, swap this for a
                dialog trigger (TODO: open create-case dialog in-place).
              */}
              <PaletteItem
                value="lista-de-casos cases workspace"
                icon={Plus}
                label={t("commandPalette.actionCasesList")}
                hint={t("commandPalette.hintWorkspace")}
                onSelect={() => navigateTo("/workspace", "cases")}
              />
              <PaletteItem
                value="nova-conversa Nova conversa chat home"
                icon={MessageSquarePlus}
                label={t("commandPalette.actionNewConversation")}
                hint={t("commandPalette.hintHome")}
                onSelect={() => navigateTo("/home", "home")}
              />
            </Command.Group>

            {/* Recentes — last 5 visited items */}
            {topRecents.length > 0 && (
              <Command.Group heading={t("commandPalette.groupRecent")} className={groupHeadingClasses}>
                {topRecents.map((r) => (
                  <PaletteItem
                    key={`recent-${r.path}`}
                    value={`recent ${r.label} ${r.path}`}
                    icon={Clock}
                    label={r.label}
                    hint={r.path}
                    onSelect={() => openRecent(r.path)}
                  />
                ))}
              </Command.Group>
            )}

            {/* Mode switching */}
            <Command.Group heading={t("commandPalette.groupGoTo")} className={groupHeadingClasses}>
              {MODE_ACTIONS.map((a) => {
                const label = t(a.labelKey);
                const hint = t(a.hintKey);
                return (
                  <PaletteItem
                    key={a.id}
                    value={`${a.id} ${label} ${hint}`}
                    icon={a.icon}
                    label={label}
                    hint={hint}
                    onSelect={() => goToMode(a)}
                  />
                );
              })}
            </Command.Group>

            {/* Cases */}
            <Command.Group
              heading={
                casesLoading
                  ? t("commandPalette.groupCasesLoading")
                  : t("commandPalette.groupCasesCount", { count: cases?.length ?? 0 })
              }
              className={groupHeadingClasses}
            >
              {(cases ?? [])
                .slice()
                .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
                .slice(0, 10)
                .map((c) => (
                  <PaletteItem
                    key={c.caseId}
                    value={`case-${c.caseId} ${c.title} ${c.status ?? ""}`}
                    icon={FileText}
                    label={c.title}
                    hint={caseStatusLabel(t, c.status)}
                    unread={(unreadByCase[c.caseId] ?? 0) > 0}
                    onSelect={() => openCase(c.caseId)}
                  />
                ))}
            </Command.Group>
          </Command.List>

          <div className="flex items-center justify-between border-t border-cream-200/60 px-3 py-2 text-caption text-cream-500 dark:border-coffee-700/60">
            <div className="flex items-center gap-3">
              <span>
                <KbdHint>↑</KbdHint>
                <KbdHint className="ml-0.5">↓</KbdHint>{" "}
                {t("commandPalette.footerNavigate")}
              </span>
              <span>
                <KbdHint>↵</KbdHint> {t("commandPalette.footerSelect")}
              </span>
            </div>
            <span>Lexiun</span>
          </div>
        </Command>
      </div>
    </Command.Dialog>
  );
}

/* ---------- Subcomponents ---------- */

interface PaletteItemProps {
  value: string; // cmdk uses this for fuzzy matching
  icon: typeof Home;
  label: string;
  hint?: string;
  onSelect: () => void;
  /** P6 item 3 — render the unread-activity dot (new lines landed on this
   *  case's thread while it was not the open chat surface). */
  unread?: boolean;
}

function PaletteItem({ value, icon: Icon, label, hint, onSelect, unread }: PaletteItemProps) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className={cn(
        "flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-body text-coffee-700 outline-none transition-colors",
        "data-[selected=true]:bg-cream-100/60 dark:text-cream-200 dark:data-[selected=true]:bg-coffee-800/60",
      )}
    >
      <Icon size={14} className="shrink-0 text-cream-500" />
      <span className="flex-1 truncate">{label}</span>
      {unread && (
        <span
          data-testid="palette-case-unread"
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500"
        />
      )}
      {hint && (
        <span className="shrink-0 truncate text-caption text-cream-500">
          {hint}
        </span>
      )}
    </Command.Item>
  );
}

function KbdHint({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        "rounded-md border border-cream-200/60 bg-cream-50 px-1.5 font-mono text-mono-sm text-coffee-500 dark:border-coffee-700/60 dark:bg-coffee-800 dark:text-cream-400",
        className,
      )}
    >
      {children}
    </kbd>
  );
}
